import { useEffect, useRef } from 'react'

/**
 * Llama a `callback` cada `intervalMs` ms, pero sólo cuando la pestaña está
 * visible (Page Visibility API). Pausa al ocultar la pestaña y reanuda al
 * volver, ejecutando un refresh inmediato al regresar para no quedar atrás.
 *
 * Uso típico:
 *   const cargar = useCallback(async ({ silent } = {}) => { ... }, [...])
 *   useEffect(() => { cargar() }, [cargar])           // carga inicial
 *   useAutoRefresh(() => cargar({ silent: true }), 30000)
 *
 * El componente debería implementar `silent` para no togglear su spinner
 * de carga durante el refresh en background (mejor UX, sin parpadeo).
 */
export function useAutoRefresh(callback, intervalMs = 30000, enabled = true) {
  const cbRef = useRef(callback)
  useEffect(() => { cbRef.current = callback }, [callback])

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined

    let timerId = null
    const tick = () => { try { cbRef.current?.() } catch {} }
    const start = () => { if (!timerId) timerId = setInterval(tick, intervalMs) }
    const stop  = () => { if (timerId) { clearInterval(timerId); timerId = null } }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') { tick(); start() }
      else { stop() }
    }

    if (document.visibilityState === 'visible') start()
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onVisibilityChange)

    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onVisibilityChange)
    }
  }, [intervalMs, enabled])
}
